<div class="plan-page__add-resources">
    <input bind:this={fileInput} type="file" {multiple} on:change={filesPicked} hidden />
    {#if !showLoading}
        <ButtonDropdown
            id={DROPDOWN_ID}
            expanded={false}
            {text}
            type={"plain"}
            textOnly={false}
            {items}
            {ariaLabel}
            on:selectedOption={optionSelected}
            on:beforeToggle={loadRecentCloudFilesForInitialisedCloudProviders}
            on:beforeToggle={loadRecentCourseFiles}
        />
    {/if}
    <div aria-live="polite" aria-atomic="true" class="plan-page__resources-loader">
        {#if showLoading}
            <div aria aria-hidden="true">
                <Button
                    id={LOADING_BUTTON_ID}
                    type="plain"
                    htmlType="button"
                    text={i18n.loading}
                    loading={true}
                />
            </div>
            <span class="screen-reader">{i18n.loading}</span>
        {/if}
    </div>
    <AddResourceErrorModal {i18n} idFocusedBeforeModal={DROPDOWN_ID} {planFilesStatusStore} />
    <div class="library-browser">
        <Modal
            titleText={i18n.library}
            confirmText={i18n.close}
            confirmButtonType="secondary"
            showCancel={false}
            enabled={planConfig.showAddFromLibrary && showLibraryBrowser}
            on:close={closeLibraryBrowser}
        >
            <iframe
                slot="body"
                bind:this={libraryFrame}
                src={libraryBrowserUrl}
                seamless="seamless"
                frameborder="0"
                title={i18n.library}
                class="library-browser__frame"
            />
        </Modal>
    </div>
    <ResourcePicker
        bind:enabled={showCourseResourcePicker}
        getResources={getResourcesForPicker}
        on:apply={onResourcePickerSave}
        {initialResources}
        rootId={0}
        i18n={resourcePickerI18n}
        initialSelectionDisabled={true}
    ></ResourcePicker>

    {#if planConfig.showAddFromReadyMadeContent}
        <ReadyMadeContentModal
            {i18n}
            bind:showAddReadyMadeContentDialog
            {availabilityOfTemplateTypes}
            courseId={courseConfig.courseId}
            organisationId={courseConfig.organisationId}
            {plan}
            elementCategory={currentElementsCategory}
            pageSize = {planConfig.readyMadeImportResourcesPageSize}
            on:importCompleted={updateElements}
        ></ReadyMadeContentModal>
    {/if}
</div>

<script>
    import ButtonDropdown from "@itslearning/prometheus/assets/dropdowns/ButtonDropdown/v1/ButtonDropdown.svelte";
    import Button from "@itslearning/prometheus/assets/inputs/Button/v1/Button.svelte";
    import Modal from "@itslearning/prometheus/assets/modals/Modal/v2/Modal.svelte";
    import ResourcePicker from "@itslearning/prometheus/assets/modals/ResourcePicker/v1/ResourcePicker.svelte";
    import AddResourceErrorModal from "../AddResourceErrorModal/AddResourceErrorModal.svelte";
    import ReadyMadeContentModal from "../ReadyMadeContent/ReadyMadeContentModal/ReadyMadeContentModal.svelte";
    import { guidV4 } from "@itslearning/atlas/generate/guid";
    import { isMobile, isChromeBook } from "@itslearning/atlas/detectors/UserAgentHelper";
    import CourseFile from "@itslearning/atlas/files/CourseFile";
    import config from "../../config";
    import { icons, elementNameMenuNameMap, elementCategory } from "../../constants";
    import { openPopup } from "../../helpers/addResourcesMenuHelper";
    import { addMenuItemFromResource } from "../../helpers/addMenuItemHelper";
    import {
        addGoogleDriveFiles,
        addOneDriveFiles,
        addLocalFiles,
        addRecentCloudFile,
        addElementIdsToPlan
    } from "../../controllers/addFileController";
    import { addElementToPlan } from "../../controllers/courseController";
    import {
        getPlan,
        getRecentCloudElements,
        getRecentElements
    } from "../../api";
    import { getCourseResources } from "../../controllers/resourceController";
    import { onMount, afterUpdate, onDestroy, createEventDispatcher } from "svelte";

    export let useGenericResourceColumn = false;
    export let isResource = false; // useful when useGenericColumn is false and we need to distinguish between Resources and Activities section
    export let plan = undefined;
    export let i18n = config.i18n;
    export let text = "";
    export let initialResources = [];
    export let ariaLabel;
    export let customAddElementIdsToPlan;
    export let planFilesStatusStore;
    export let courseConfig;
    export let availabilityOfTemplateTypes = [];

    const planConfig = plan.resources ? plan.resources : config;
    const cloudSettings = planConfig.cloudSettings ? planConfig.cloudSettings : config.cloudSettings;
    const dispatch = createEventDispatcher();
    const aiTools = useGenericResourceColumn ? config.aiToolsCombined : isResource ? config.aiToolsResource : config.aiToolsActivity;

    const currentElementsCategory = useGenericResourceColumn
        ? null
        : isResource
            ? elementCategory.Resource
            : elementCategory.Activity;

    const columnId = useGenericResourceColumn
        ? planConfig.elementColumnId
        : isResource
          ? planConfig.resourceColumnId
          : planConfig.activityColumnId;

    let elementsThatWereMovedToPlanFolderMessage = [];
    let items = [];
    let fileInput;

    const multiple = true;

    const fileUploadOption = {
        label: isMobile() ? i18n.device : i18n.computer,
        link: false,
        value: { source: "Computer" },
        cssClass: "quick-add__" + (isMobile() ? "device" : "computer")
    };

    let showLibraryBrowser = false;
    let libraryBrowserUrl = "";
    let libraryFrame;

    let showCourseResourcePicker = false;

    let showAddReadyMadeContentDialog = false;

    let recentCloudFiles = new Map();
    let recentCourseFiles = {};

    const GOOGLE_DRIVE_CLOUD_PROVIDER = "googleDrive";

    const contentElementType =
        planConfig.librarySettings !== null
            ? useGenericResourceColumn
                ? planConfig.librarySettings.contentElementTypes.all
                : isResource
                  ? planConfig.librarySettings.contentElementTypes.resources
                  : planConfig.librarySettings.contentElementTypes.activities
            : null;

    const showAllItem = {
        label: i18n.showAll,
        link: false,
        value: { source: "Show All" },
        cssClass: "quick-add__show-all"
    };

    const DROPDOWN_ID = `dropdown-${guidV4()}`;
    const LOADING_BUTTON_ID = `loading-button-${guidV4()}`;

    let addNewItems;

    $: {
        const items = useGenericResourceColumn
            ? planConfig.combinedItems
            : isResource
              ? planConfig.resourceItems
              : planConfig.activityItems;

        addNewItems = items.map(resource =>
            addMenuItemFromResource(
                resource,
                planConfig.staticContentPath,
                recentCourseFiles,
                i18n
            )
        );
    }

    const subMenuItems = Array.isArray(aiTools) ? aiTools.map(e => ({
        icon: e.icon,
        iconsize: 24,
        label: e.title,
        link: false,
        value: { source: "Ai", url: e.url },
        url: e.url,
    })) : null;

    $: addAiTools = Array.isArray(aiTools)
        ? {
                icon: planConfig.staticContentPath + planConfig.aiIcon,
                label: i18n.aiTools,
                link: false,
                value: { source: "Ai" },
                items: [
                    {
                        options: subMenuItems,
                    }
                ],
                cssClass: "quick-add__item-icon"
          }
        : null;

    $: addFromLibraryItem = planConfig.showAddFromLibrary
        ? {
              icon: planConfig.staticContentPath + icons.library,
              label: i18n.library,
              link: false,
              value: { source: "Library" },
              cssClass: "quick-add__item-icon"
          }
        : null;

    $: addFromReadyMadeContent = planConfig.showAddFromReadyMadeContent
        ? {
              icon: planConfig.staticContentPath + icons.readyMadeContent,
              label: i18n.readyMadeContent,
              link: false,
              value: { source: "ReadyMadeContent" },
              cssClass: "quick-add__item-icon"
          }
        : null;

    $: addFromCourse = {
        icon: `${planConfig.staticContentPath}${icons.course}`,
        label: i18n.course,
        link: false,
        value: { source: "Course" },
        cssClass: "quick-add__item-icon"
    };

    $: showAddFromCloud = useGenericResourceColumn || isResource;
    $: showAddFromGoogleDrive =
        showAddFromCloud &&
        cloudSettings.cloudServiceSettings.googleDrive.showCloudService;
    $: showAddFromOneDrive =
        showAddFromCloud && cloudSettings.cloudServiceSettings.oneDrive.showCloudService;

    $: resourcePickerI18n = {
        ...i18n,
        folderEmptyState: useGenericResourceColumn
            ? i18n.folderEmpty
            : isResource
              ? i18n.folderDoesNotContainResources
              : i18n.folderDoesNotContainActivities
    };

    $: {
        const menuOptions = [];

        menuOptions.push(...addNewItems);

        if (addAiTools) {
            menuOptions.push(addAiTools);
        }

        if (planConfig.showAddFromCourse) {
            menuOptions.push(addFromCourse);
        }

        if (planConfig.showAddFromReadyMadeContent) {
            if (availabilityOfTemplateTypes.showYourCourses
            || availabilityOfTemplateTypes.showCourseTemplates
            || availabilityOfTemplateTypes.showFromPublishers
            || availabilityOfTemplateTypes.showOtherSites) {
                menuOptions.push(addFromReadyMadeContent);
            }
        }

        if (planConfig.librarySettings !== null) {
            menuOptions.push(addFromLibraryItem);
        }

        if (showAddFromGoogleDrive) {
            // Default menu items
            const menuItems = [
                {
                    options: [
                        {
                            label: i18n.selectFileLabel,
                            link: false,
                            value: { source: "GoogleDrive" },
                            divider: true,
                            ellipsis: true
                        }
                    ]
                }
            ];

            const recentFiles = recentCloudFiles.get(GOOGLE_DRIVE_CLOUD_PROVIDER);

            if (recentFiles && recentFiles.length > 0) {
                const subMenuOptions = [];

                menuItems.push({
                    label: i18n.recent,
                    options: subMenuOptions
                });

                const getCloudFileIcon = mimeType => {
                    const cloudFileIcon = cloudSettings.cloudFileIcons[mimeType];

                    return cloudFileIcon
                        ? cloudFileIcon
                        : `${planConfig.staticContentPath}${icons.googleDriveDefault}`;
                };

                recentFiles.forEach(recentFile => {
                    subMenuOptions.push({
                        icon: getCloudFileIcon(recentFile.mimeType),
                        iconsize: 24,
                        value: {
                            source: "RecentGoogleDrive",
                            elementId: recentFile.id,
                            cloudProvider: GOOGLE_DRIVE_CLOUD_PROVIDER
                        },
                        label: recentFile.name,
                        link: false,
                        cssClass: "quick-add__item-icon"
                    });
                });
            }

            menuOptions.push({
                icon: planConfig.staticContentPath + icons.googleDrive,
                label: i18n.googleDrive,
                link: false,
                items: menuItems,
                value: { source: "GoogleDrive" },
                cssClass: "quick-add__item-icon"
            });
        }

        if (showAddFromOneDrive) {
            const icon = cloudSettings.showOffice365InsteadOfOneDrive
                ? icons.office365
                : icons.onedrive;
            const label = cloudSettings.showOffice365InsteadOfOneDrive
                ? i18n.office365
                : i18n.oneDrive;

            menuOptions.push({
                icon: `${planConfig.staticContentPath}${icon}`,
                iconsize: 24,
                label,
                link: false,
                value: { source: "OneDrive" },
                cssClass: "quick-add__item-icon"
            });
        }

        // Only show these options on the combined and resources element type menus
        if ((useGenericResourceColumn || isResource) && !isChromeBook()) {
            menuOptions.push(fileUploadOption);
        }

        menuOptions.push(showAllItem);

        items = [{ options: menuOptions }];
    }

    let showLoading = false;

    const unsubscribeFromPlanFilesStatusStore = planFilesStatusStore.subscribe(statuses => {
        showLoading = Array.from(statuses.values()).some(
            file =>
                !file.error &&
                !file.success &&
                file.columnId === columnId &&
                file.planId === plan.id
        );
    });

    $: {
        if (planConfig.librarySettings !== null) {
            const requestItems = planConfig.librarySettings.requestItems;

            let url = `${planConfig.librarySettings.libraryUrl}&${requestItems.customId}=${columnId}&${requestItems.elementType}=${contentElementType}`;

            if (plan.learningObjectives.some(lo => lo.isFromRepository)) {
                const loIds = plan.learningObjectives
                    .filter(lo => lo.isFromRepository)
                    .map(lo => lo.id)
                    .join();

                url = `${url}&${requestItems.learningObjectiveId}=${loIds}`;
            }

            libraryBrowserUrl = url;
        }
    }

    $: {
        if (planConfig.showAddFromLibrary && showLibraryBrowser) {
            window.addEventListener("message", framePostMessageHandler, false);
        } else {
            window.removeEventListener("message", framePostMessageHandler);
        }
    }

    async function optionSelected(event) {
        const data = event.detail.data;

        const updateCloudElementsInfo = async () => {
            await updateElements();
            await loadRecentCloudFilesForInitialisedCloudProviders();
        };

        showLoading = true;
        switch (data.source) {
            case "Element":
                openPopup(data.url);
                break;
            case "Ai":
                openPopup(data.url);
                break;
            case "RecentElement":
                await addElements(data);
                break;

            case "GoogleDrive": {
                await addGoogleDriveFiles(
                    cloudSettings.cloudServiceSettings.googleDrive,
                    plan.id,
                    columnId,
                    plan.resources ? plan.resources : null,
                    planFilesStatusStore
                );
                await updateCloudElementsInfo();
                break;
            }

            case "RecentGoogleDrive": {
                const cloudFile = recentCloudFiles
                    .get(data.cloudProvider)
                    .find(cloudFile => cloudFile.id === data.elementId);

                await addRecentCloudFile(
                    cloudFile,
                    plan.id,
                    columnId,
                    plan.resources ? plan.resources : null,
                    planFilesStatusStore
                );
                await updateCloudElementsInfo();
                break;
            }

            case "OneDrive": {
                await addOneDriveFiles(
                    cloudSettings.cloudServiceSettings.oneDrive,
                    plan.id,
                    columnId,
                    plan.resources ? plan.resources : null,
                    planFilesStatusStore
                );
                await updateElements();
                break;
            }

            case "Course":
                showCourseResourcePicker = true;
                break;
            case "ReadyMadeContent":
                showAddReadyMadeContentDialog = true;
                break;
            case "Library":
                showLibraryBrowser = true;
                break;
            case "Computer":
                fileInput.click();
                break;
            case "Show All":
                useGenericResourceColumn
                    ? openPopup(planConfig.showAllUrl)
                    : isResource
                      ? openPopup(planConfig.showAllResourcesUrl)
                      : openPopup(planConfig.showAllActivitiesUrl);
                break;
            default:
                break;
        }

        showLoading = false;
        dispatch("resourceOrActivityIsSelected");
    }

    async function onResourcePickerSave({ detail: items }) {

        if (!items || items.length === 0) {
            return;
        }

        const elementsIdsToUpdate = items
            .filter(
                item =>
                    !initialResources.some(
                        alreadyAddedResource => alreadyAddedResource.id === item.id
                    )
            )
            .map(item => item.id);

        await addElementIdsToPlanAndBuildMessages(elementsIdsToUpdate, plan.id, columnId, true);
        await updateElements();
    }

    async function addElementIdsToPlanAndBuildMessages (elementsIdsToUpdate, planId, columnId, partialUpdate) {
        const response = await addElementIdsToPlan(elementsIdsToUpdate, planId, columnId, partialUpdate, plan?.topic?.id ?? 0);
        await setMovedToPlanFolderMessages(response);
    }

    async function addElements(data) {
        const response = customAddElementIdsToPlan
            ? await customAddElementIdsToPlan(data.elementId, plan.id, columnId, plan?.topic?.id ?? 0)
            : await addElementToPlan(data.elementId, plan.id, columnId, plan?.topic?.id ?? 0);

        await setMovedToPlanFolderMessages(response);
        await updateElements();
    }

    async function setMovedToPlanFolderMessages(response) {
        elementsThatWereMovedToPlanFolderMessage = [];
        if (response?.ElementsThatWereMovedToPlanFolderMessage) {
            elementsThatWereMovedToPlanFolderMessage.push(
                ...response.ElementsThatWereMovedToPlanFolderMessage
            );
        }
    }

    async function updateElements() {
        const updatedPlan = await getPlan(plan.id);

        dispatch("resourceAdded", { updatedPlan, elementsThatWereMovedToPlanFolderMessage });
    }

    async function filesPicked() {
        const files = [];

        for (let i = 0; i < fileInput.files.length; i++) {
            files.push(fileInput.files[i]);
        }

        if (files.length > 0) {
            await addLocalFiles(
                files,
                plan.id,
                columnId,
                plan.resources ? plan.resources : null,
                planFilesStatusStore
            );
            await updateElements();

            // Clear the file input after getting the files or else if the user
            // chooses the same file again the on:change event will not fire
            fileInput.value = null;
        }
    }

    function closeLibraryBrowser() {
        showLibraryBrowser = false;
    }

    function getResourcesForPicker(folderId, pageIndex) {
        return getCourseResources(
            folderId,
            useGenericResourceColumn,
            isResource,
            pageIndex,
            plan.resources
        );
    }

    async function framePostMessageHandler(event) {
        if (!event.data) {
            return;
        }

        if (event.source !== libraryFrame.contentWindow) {
            return;
        }

        try {
            const data = JSON.parse(event.data);

            if (
                !data.messageName ||
                data.messageName !==
                    planConfig.librarySettings.postMessages.sendElementIdToCourseFeature
            ) {
                return;
            }

            if (data.relatedEntityId == plan.id && data.customId == columnId) {
                await updateElements();
            }
        } catch {
            // do nothing
        }
    }

    async function loadRecentCloudFilesForInitialisedCloudProviders() {
        const cloudServiceSettings = cloudSettings.cloudServiceSettings;
        const cloudServices = Object.keys(cloudServiceSettings);

        const loadRecentFilesTasks = new Map(
            cloudServices
                .filter(
                    cloudService =>
                        cloudServiceSettings[cloudService].showCloudService &&
                        cloudServiceSettings[cloudService].recentCloudElementsSettings.enabled
                )
                .map(cloudService => [
                    cloudService,
                    getRecentCloudElements(
                        cloudService,
                        cloudServiceSettings[cloudService].recentCloudElementsSettings
                    )
                ])
        );

        const recentFiles = new Map();

        for (const [cloudService, loadRecentFilesTask] of loadRecentFilesTasks) {
            recentFiles.set(cloudService, await loadRecentFilesTask);
        }

        recentCloudFiles = recentFiles;
    }

    async function loadRecentCourseFiles() {
        const itemTypes = useGenericResourceColumn
            ? planConfig.combinedItems
            : planConfig.activityItems.concat(planConfig.resourceItems);

        let elementSelector = 0;

        // Map the elements from the resource and/or activity items to their
        // course element types because that's what the endpoint needs
        itemTypes.forEach(itemType => {
            elementSelector = elementSelector | itemType.elementId;
        });

        const recentFiles = {};
        const recents = await getRecentElements(elementSelector);

        scrollDownOnAddResourceMenu();

        recents.forEach(element => {
            const courseFile = CourseFile.fromApi(element);

            // Map the ElementType returned to the values of the menu items
            const keys = elementNameMenuNameMap[element.ElementType];

            if (keys) {
                keys.forEach(key => {
                    if (!recentFiles[key]) {
                        recentFiles[key] = [];
                    }

                    recentFiles[key].push(courseFile);
                });
            }

            recentCourseFiles = recentFiles;
        });
    }

    onMount(async () => {
        await loadRecentCloudFilesForInitialisedCloudProviders();
    });

    afterUpdate(() => {
        if (showLoading) {
            document.getElementById(LOADING_BUTTON_ID).focus();
        }
    });

    onDestroy(() => {
        window.removeEventListener("message", framePostMessageHandler);
        unsubscribeFromPlanFilesStatusStore();
    });

    const resourcesActivities = ".plan-page__field-elements";
    const resources = ".plan-page__field-resources";
    const activities = ".plan-page__field-activities";
    const expanded = " section[aria-expanded='true']";
    const pageHeader = ".plan-page__field.plan-page__field";
    const menuSection = " section[aria-expanded='true'] li";
    const elementsSection = "-elements";
    const resourcesSection = "-resources";
    const activitiesSection = "-activities";
    const sectionExpanded = ".plan-page__add-resources section[aria-expanded='true']";
    const addSecction = " .plan-page__add-resources";

    function scrollDownOnAddResourceMenu() {
        if (config.librarySettings) {
            if (useGenericResourceColumn) {
                if (document.querySelector(resourcesActivities + expanded) !== null) {
                    document.querySelector(resourcesActivities + menuSection).scrollIntoView();
                } else {
                    document
                        .querySelector(pageHeader + elementsSection + addSecction)
                        .scrollIntoView();
                }
            } else if (isResource) {
                if (document.querySelector(resources + expanded) !== null) {
                    document.querySelector(resources + menuSection).scrollIntoView();
                } else {
                    document
                        .querySelector(pageHeader + resourcesSection + addSecction)
                        .scrollIntoView();
                }
            } // Actvity
            else {
                if (document.querySelector(activities + expanded) !== null) {
                    document.querySelector(activities + menuSection).scrollIntoView();
                } else {
                    document
                        .querySelector(pageHeader + activitiesSection + addSecction)
                        .scrollIntoView();
                }
            }
        }
    }

    window.addEventListener("click", function () {
        if (document.querySelector(sectionExpanded) !== null) {
            if (useGenericResourceColumn) {

                if (document.querySelector(resourcesActivities + expanded) !== null) {
                    document
                        .querySelector(pageHeader + elementsSection + addSecction)
                        .scrollIntoView();
                }
            } else if (isResource) {
                if (document.querySelector(resources + expanded) !== null) {
                    document
                        .querySelector(pageHeader + resourcesSection + addSecction)
                        .scrollIntoView();
                }
            } // Activity
            else {
                if (document.querySelector(activities + expanded) !== null) {
                    document
                        .querySelector(pageHeader + activitiesSection + addSecction)
                        .scrollIntoView();
                }
            }
        }
    });
</script>
